import React, { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Download, Upload, FileSpreadsheet, FileJson, FileText, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import {
  getExportableEntities,
  exportEntity,
  downloadTemplate,
  importEntityFile,
  EntityInfo,
  ImportResult,
} from '../../api/import-export';

type Tab = 'export' | 'import';
type Format = 'csv' | 'json' | 'xlsx';

export default function ImportExportSection() {
  const [activeTab, setActiveTab] = useState<Tab>('export');

  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
      <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)' }}>
        <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>Import / Export Dati</h3>
      </div>

      {/* Tab toggle */}
      <div style={{ display: 'flex', gap: '0.375rem', padding: '0.875rem 1.25rem 0.5rem' }}>
        <TabBtn active={activeTab === 'export'} onClick={() => setActiveTab('export')} icon={<Download size={14} />} label="Esporta" />
        <TabBtn active={activeTab === 'import'} onClick={() => setActiveTab('import')} icon={<Upload size={14} />} label="Importa" />
      </div>

      <div style={{ padding: '1.25rem' }}>
        {activeTab === 'export' ? <ExportPanel /> : <ImportPanel />}
      </div>
    </div>
  );
}

// ─── Tab Button ─────────────────────────────────────────────────────────────

function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
        padding: '0.5rem 1rem',
        background: active ? 'var(--color-primary-highlight)' : 'transparent',
        border: active ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
        borderRadius: 'var(--radius-interactive)',
        cursor: 'pointer',
        color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
        fontSize: 'var(--text-sm)', fontWeight: active ? 600 : 400,
        transition: 'all var(--spring-duration) var(--spring-easing)',
      }}
    >
      {icon} {label}
    </button>
  );
}

// ─── Export Panel ───────────────────────────────────────────────────────────

function ExportPanel() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<Format>('csv');

  const { data: entities, isLoading } = useQuery({
    queryKey: ['import-export-entities'],
    queryFn: () => getExportableEntities(),
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      for (const key of selected) {
        await exportEntity(key, format);
      }
    },
    onSuccess: () => {
      toast.success(`Export di ${selected.size} entità completato`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleEntity = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (!entities) return;
    setSelected(prev => prev.size === entities.length ? new Set() : new Set(entities.map(e => e.key)));
  };

  if (isLoading) return <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento...</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Entity selection */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>Seleziona entità</span>
          <button onClick={selectAll} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}>
            {selected.size === entities?.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {entities?.map(e => (
            <label
              key={e.key}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                background: selected.has(e.key) ? 'var(--color-primary-alpha)' : 'var(--glass-bg-card)',
                border: `1px solid ${selected.has(e.key) ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                borderRadius: 'var(--radius-card)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                color: selected.has(e.key) ? 'var(--color-primary)' : 'var(--color-text)',
                transition: 'all 0.15s',
              }}
            >
              <input type="checkbox" checked={selected.has(e.key)} onChange={() => toggleEntity(e.key)} style={{ display: 'none' }} />
              {e.displayName}
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>({e.count})</span>
            </label>
          ))}
        </div>
      </div>

      {/* Format selection */}
      <div>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>Formato</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {([
            { value: 'csv' as Format, label: 'CSV', icon: <FileText size={14} /> },
            { value: 'json' as Format, label: 'JSON', icon: <FileJson size={14} /> },
            { value: 'xlsx' as Format, label: 'Excel', icon: <FileSpreadsheet size={14} /> },
          ]).map(f => (
            <button
              key={f.value}
              onClick={() => setFormat(f.value)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                background: format === f.value ? 'var(--color-primary-alpha)' : 'var(--glass-bg-card)',
                border: `1px solid ${format === f.value ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                borderRadius: 'var(--radius-card)',
                color: format === f.value ? 'var(--color-primary)' : 'var(--color-text)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
              }}
            >
              {f.icon} {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Export button */}
      <Button
        onClick={() => exportMutation.mutate()}
        disabled={selected.size === 0}
        loading={exportMutation.isPending}
        icon={<Download size={14} />}
      >
        Esporta {selected.size > 0 ? `(${selected.size})` : ''}
      </Button>
    </div>
  );
}

// ─── Import Panel ───────────────────────────────────────────────────────────

function ImportPanel() {
  const [entity, setEntity] = useState('');
  const [mode, setMode] = useState<'create' | 'upsert'>('create');
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: entities } = useQuery({
    queryKey: ['import-export-entities'],
    queryFn: () => getExportableEntities(),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => importEntityFile(entity, file, mode),
    onSuccess: (res) => {
      setResult(res);
      if (res.errors.length === 0) {
        toast.success(`${res.imported} record importati con successo`);
      } else {
        toast.warning(`${res.imported} importati, ${res.errors.length} errori`);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleFile = (file: File) => {
    if (!entity) {
      toast.error('Seleziona prima un\'entità');
      return;
    }
    setResult(null);
    importMutation.mutate(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const selectedEntity = entities?.find(e => e.key === entity);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Entity select */}
      <div>
        <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, display: 'block', marginBottom: '0.375rem' }}>Entità</label>
        <div style={{ position: 'relative' }}>
          <select
            value={entity}
            onChange={e => { setEntity(e.target.value); setResult(null); }}
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
              appearance: 'none',
            }}
          >
            <option value="">Seleziona entità...</option>
            {entities?.map(e => (
              <option key={e.key} value={e.key}>{e.displayName} ({e.count})</option>
            ))}
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
        </div>
      </div>

      {/* Mode select */}
      <div>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, display: 'block', marginBottom: '0.375rem' }}>Modalità</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {([
            { value: 'create' as const, label: 'Crea nuovi', desc: 'Aggiunge solo record nuovi' },
            { value: 'upsert' as const, label: 'Aggiorna esistenti', desc: selectedEntity?.upsertField ? `Upsert su ${selectedEntity.upsertField}` : 'Non disponibile' },
          ]).map(m => (
            <button
              key={m.value}
              disabled={m.value === 'upsert' && !selectedEntity?.upsertField}
              onClick={() => setMode(m.value)}
              style={{
                flex: 1,
                display: 'flex', flexDirection: 'column', gap: '0.125rem',
                padding: '0.5rem 0.75rem',
                background: mode === m.value ? 'var(--color-primary-alpha)' : 'var(--glass-bg-card)',
                border: `1px solid ${mode === m.value ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                borderRadius: 'var(--radius-card)',
                cursor: m.value === 'upsert' && !selectedEntity?.upsertField ? 'not-allowed' : 'pointer',
                opacity: m.value === 'upsert' && !selectedEntity?.upsertField ? 0.5 : 1,
                color: mode === m.value ? 'var(--color-primary)' : 'var(--color-text)',
                fontSize: 'var(--text-sm)',
                textAlign: 'left',
              }}
            >
              <span style={{ fontWeight: 500 }}>{m.label}</span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{m.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '0.5rem',
          padding: '2rem',
          border: `2px dashed ${dragOver ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
          borderRadius: 'var(--radius-card)',
          background: dragOver ? 'var(--color-primary-alpha)' : 'var(--glass-bg-card)',
          cursor: entity ? 'pointer' : 'not-allowed',
          opacity: entity ? 1 : 0.5,
          transition: 'all 0.15s',
        }}
      >
        <Upload size={24} style={{ color: 'var(--color-text-muted)' }} />
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {entity ? 'Trascina il file qui o clicca per selezionare' : 'Seleziona prima un\'entità'}
        </span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>CSV, JSON o XLSX — max 50MB</span>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.json,.xlsx"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
      </div>

      {/* Import progress */}
      {importMutation.isPending && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', textAlign: 'center' }}>
          Importazione in corso...
        </div>
      )}

      {/* Template download */}
      {entity && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FileText size={14} />}
            onClick={() => downloadTemplate(entity, 'csv')}
          >
            Template CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<FileSpreadsheet size={14} />}
            onClick={() => downloadTemplate(entity, 'xlsx')}
          >
            Template Excel
          </Button>
        </div>
      )}

      {/* Import result */}
      {result && (
        <div style={{
          padding: '1rem',
          background: result.errors.length === 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 191, 36, 0.1)',
          border: `1px solid ${result.errors.length === 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
          borderRadius: 'var(--radius-card)',
          display: 'flex', flexDirection: 'column', gap: '0.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {result.errors.length === 0
              ? <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
              : <AlertCircle size={16} style={{ color: 'var(--color-warning)' }} />
            }
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
              {result.imported} record importati{result.errors.length > 0 ? `, ${result.errors.length} errori` : ''}
            </span>
          </div>
          {result.errors.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {result.errors.slice(0, 10).map((err, i) => (
                <div key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  <span style={{ fontWeight: 500 }}>Riga {err.row}:</span> {err.message}
                </div>
              ))}
              {result.errors.length > 10 && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                  ... e altri {result.errors.length - 10} errori
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
